/**
 * Phone Input Component with Country Selector
 * Provides phone number input with country code dropdown
 */

import { useState, useEffect } from 'react';
import { Input } from './input';
import { Label } from './label';
import {
  COMMON_COUNTRY_CODES,
  validatePhone,
  detectCountryFromLocale,
  type CountryCode,
} from '@leal-crm/shared/utils/phoneUtils';

interface PhoneInputProps {
  value: string;
  countryCode: CountryCode;
  onValueChange: (phone: string) => void;
  onCountryChange: (country: CountryCode) => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  autoDetectCountry?: boolean;
}

// Build dial codes and country names from COMMON_COUNTRY_CODES
const DIAL_CODES: Record<string, string> = Object.fromEntries(
  COMMON_COUNTRY_CODES.map((c) => [c.code, `+${c.callingCode}`])
);

const COUNTRY_NAMES: Record<string, string> = Object.fromEntries(
  COMMON_COUNTRY_CODES.map((c) => [c.code, c.name])
);

export function PhoneInput({
  value,
  countryCode,
  onValueChange,
  onCountryChange,
  label,
  error,
  required = false,
  disabled = false,
  placeholder,
  autoDetectCountry = true,
}: PhoneInputProps) {
  const [localError, setLocalError] = useState<string | undefined>(error);

  // Auto-detect country on mount
  useEffect(() => {
    if (autoDetectCountry && !value) {
      const locale = navigator.language || 'es-ES';
      const detected = detectCountryFromLocale(locale);
      if (detected !== countryCode) {
        onCountryChange(detected);
      }
    }
  }, [autoDetectCountry, countryCode, onCountryChange, value]);

  // Update error when prop changes
  useEffect(() => {
    setLocalError(error);
  }, [error]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Allow only digits and some formatting characters
    const cleaned = newValue.replace(/[^\d\s\-()]/g, '');
    onValueChange(cleaned);

    // Clear error on change
    if (localError) {
      setLocalError(undefined);
    }
  };

  const handleBlur = () => {
    if (value) {
      const isValid = validatePhone(value, countryCode);
      if (!isValid) {
        setLocalError('Numero de telefono invalido');
      }
    }
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onCountryChange(e.target.value as CountryCode);
    // Clear error when country changes
    setLocalError(undefined);
  };

  const displayError = localError || error;
  const dialCode = DIAL_CODES[countryCode] || '+34';
  const defaultPlaceholder = placeholder || `${dialCode} 600 000 000`;

  return (
    <div className="space-y-1.5">
      {label && (
        <Label htmlFor="phone-input">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      <div className="flex gap-2">
        <select
          value={countryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          className="h-10 w-28 rounded-md border border-input bg-background px-2 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {COMMON_COUNTRY_CODES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.code} +{country.callingCode}
            </option>
          ))}
        </select>
        <Input
          id="phone-input"
          type="tel"
          value={value}
          onChange={handlePhoneChange}
          onBlur={handleBlur}
          placeholder={defaultPlaceholder}
          disabled={disabled}
          className={`flex-1 ${displayError ? 'border-red-500' : ''}`}
        />
      </div>
      {displayError && <p className="text-red-500 text-sm">{displayError}</p>}
      <p className="text-xs text-gray-400">
        {COUNTRY_NAMES[countryCode]} ({DIAL_CODES[countryCode]})
      </p>
    </div>
  );
}

export default PhoneInput;
